import { useState } from "react";
import { ClassItem } from "@/data/classes";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, DollarSign, Users, MapPin, ExternalLink, ShoppingBag, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import InterestForm from "@/components/InterestForm";
import AdditionalTimeOptions from "@/components/AdditionalTimeOptions";
import { useScheduleOptions } from "@/hooks/useScheduleOptions";
import ClassRegistrationForm from "@/components/ClassRegistrationForm";
import { useNavigate } from "react-router-dom";
import { useNextRegistrationPeriodLabel } from "@/hooks/useNextRegistrationPeriod";
import { formatPublicSchedule, publicPeriodSuffix } from "@/lib/publicScheduleDisplay";
import {
  describeSemesterForClass,
  getSemesterMonthCount,
  getSemesterWindowForWeekday,
  semesterIdFromLabel,
  type WeekdayName,
} from "@/lib/academicCalendar";
import { parseWeekdays } from "@/lib/sessionSchedule";
import {
  formatSemesterSessionLabel,
  publicPriceLabel,
  coerceBillingUnit,
} from "@/lib/classPricing";
import { semesterTotalForSession } from "@/lib/semesterSessionPrice";
import { resolveClassDisplayLocation } from "@/lib/classLocation";
import { useResolvedInstructor } from "@/lib/instructorResolver";
import { publicCtaFor } from "@/lib/publicVisibility";


/**
 * Auto-fill semester dates when a session labeled "Fall Semester" /
 * "Spring Semester" has no explicit dates. Reads from the central academic
 * calendar so parents always see accurate ranges without staff typing them.
 */
function autoSemesterDates(
  sessionLabel: string,
  sessionDates: string | undefined,
  classSchedule: string | undefined,
): string | null {
  const semester = semesterIdFromLabel(sessionLabel);
  if (!semester) return null;
  const weekdays = [
    ...parseWeekdays(sessionDates),
    ...parseWeekdays(classSchedule),
  ] as WeekdayName[];
  if (weekdays.length === 0) return null;
  const desc = describeSemesterForClass(semester, weekdays);
  if (!desc) return null;
  return desc.lines.map((l) => l.text).join(" · ");
}

/** Compute the semester per-child total for a session, using the shared resolver. */
function semesterTotalFor(
  sessionLabel: string,
  sessionDates: string | undefined,
  classItem: ClassItem,
  sessionPrice?: number | null,
): number | null {
  return semesterTotalForSession(classItem, {
    label: sessionLabel,
    dates: sessionDates,
    price: sessionPrice ?? null,
  });
}


interface Props {
  classItem: ClassItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ClassDetailDialog = ({ classItem, open, onOpenChange }: Props) => {
  const [supplyOpen, setSupplyOpen] = useState(false);
  const navigate = useNavigate();
  const isExternal = classItem?.managementType === "external_provider";
  const isComingSoon = classItem?.status === "coming-soon" && !isExternal;
  const isInstructorWanted = classItem?.status === "instructor-wanted" && !isExternal;
  const isWaitlist = classItem?.status === "waitlist";
  const ctaRules = publicCtaFor(classItem?.status ?? "ended", isExternal);

  const nextPeriodLabel = useNextRegistrationPeriodLabel();
  // When a Coming Soon class publishes interest-only groups (e.g. Middle School
  // vs High School), interest must be captured per group — never in a single
  // generic bucket. The group panel replaces the generic interest form.
  const { options: scheduleOptions } = useScheduleOptions(classItem?.dbId ?? null);
  const { data: resolvedInstructor } = useResolvedInstructor((classItem?.id ?? "").replace(/^db:/, "") || null);
  const publicInstructorName =
    resolvedInstructor && !resolvedInstructor.is_placeholder
      ? resolvedInstructor.full_name || resolvedInstructor.display_name
      : null;
  const hasInterestGroups = scheduleOptions.some((o) => o.status === "interest_only");
  if (!classItem) return null;
  const instructorWanted = isInstructorWanted;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-background max-h-[85vh] overflow-y-auto">
        <div className="relative h-56 -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-lg">
          <img src={classItem.image} alt={classItem.title} className="w-full h-full object-cover" />
        </div>
        <DialogHeader>
          <Badge className="w-fit bg-primary text-primary-foreground mb-2">{classItem.category}</Badge>
          <DialogTitle className="font-heading text-2xl">{classItem.title}</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
          {classItem.description.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
            /^https?:\/\//.test(part) ? (
              <a
                key={i}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:no-underline break-all"
              >
                {part}
              </a>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
        </p>

        {classItem.weeklyBreakdown && classItem.weeklyBreakdown.length > 0 && (
          <div className="mt-4 space-y-3">
            <h4 className="font-heading text-lg text-foreground">Weekly Breakdown</h4>
            <ul className="space-y-2">
              {classItem.weeklyBreakdown.map((week, i) => (
                <li key={i} className="bg-card border border-border rounded-md p-3 text-sm text-muted-foreground leading-relaxed">
                  {week}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-4">
          <InfoRow icon={<Users size={16} />} label="Ages" value={classItem.ages} />
          <InfoRow icon={<Calendar size={16} />} label="Duration" value={classItem.duration} />
          <InfoRow
            icon={<DollarSign size={16} />}
            label="Price"
            value={publicPriceLabel({
              pricePerChild: classItem.unitPrice,
              billingUnit: coerceBillingUnit(classItem.billingUnit),
              priceDisplay: classItem.price,
              externalPayment: classItem.managementType === "external_provider",
              hasInstructor:
                classItem.managementType === "external_provider"
                  ? true
                  : classItem.hasInstructor,
            })}
          />
          <InfoRow
            icon={<MapPin size={16} />}
            label="Location"
            value={resolveClassDisplayLocation(classItem)}
          />
          {/* Instructor comes from the canonical assignment resolver only.
              Nothing is shown when no real assignment exists. */}
          {publicInstructorName && (
            <InfoRow icon={<Users size={16} />} label="Instructor" value={publicInstructorName} />
          )}
        </div>

        {classItem.sessions && classItem.sessions.length > 0 ? (
          <div className="mt-4 space-y-2">
            <h4 className="font-heading text-lg text-foreground flex items-center gap-2">
              <Calendar size={18} className="text-primary" /> Sessions
            </h4>
            <ul className="space-y-2">
              {classItem.sessions.map((s) => {
                const autoDates = autoSemesterDates(s.label, s.dates, classItem.schedule);
                const showDates = (s.dates && s.dates.trim()) || autoDates;
                const semesterTotal = semesterTotalFor(s.label, s.dates, classItem, s.price);
                const displayLabel = formatSemesterSessionLabel(s.label, semesterTotal);
                return (
                <li
                  key={s.label}
                  className="bg-card border border-border rounded-md p-3 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
                >
                  <div>
                    <p className="font-body font-bold text-foreground">{displayLabel}</p>
                    {showDates && <p className="text-muted-foreground">{showDates}</p>}
                  </div>
                  {s.closed ? (
                    <Badge variant="outline" className="w-fit">Closed to new registration</Badge>
                  ) : s.full ? (
                    <Badge variant="secondary" className="w-fit">Full</Badge>
                  ) : null}
                </li>
                );
              })}
            </ul>
          </div>

        ) : (
          <div className="grid grid-cols-2 gap-3 mt-4">
            <InfoRow icon={<Clock size={16} />} label="Schedule" value={formatPublicSchedule(classItem)} />
            <InfoRow icon={<Calendar size={16} />} label="Starts" value={classItem.startDate} />
          </div>
        )}
        {classItem.supplyList && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setSupplyOpen(true)}
            className="w-full mt-4 font-body font-bold"
          >
            <ShoppingBag size={16} className="mr-2" />
            Supply List
          </Button>
        )}
        <p className="text-xs text-muted-foreground mt-4 italic">
          Parents are welcome to drop off or stay during class. Small class sizes — no more than 10 students.
        </p>

        <AdditionalTimeOptions
          dbClassId={classItem.dbId}
          publicClassId={classItem.id}
          className={classItem.title}
          classItem={classItem}
        />
        {isExternal ? (
          <div className="mt-6 rounded-lg border border-sky-200 bg-sky-50 p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Badge className="bg-sky-100 text-sky-900 border-sky-300 border">Privately Managed</Badge>
            </div>
            <p className="text-sm text-sky-900">
              {classItem.providerPublicContactText ??
                "Registration and instruction are managed directly by the class provider."}
              {classItem.providerName ? ` (${classItem.providerName})` : ""}
            </p>
            {/* Internal provider contact email/phone are staff-only and are never shown publicly. */}

            {classItem.externalSignupUrl ? (
              <Button asChild className="w-full font-body font-bold">
                <a href={classItem.externalSignupUrl} target="_blank" rel="noopener noreferrer">
                  Register with Provider <ExternalLink size={16} className="ml-2" />
                </a>
              </Button>
            ) : (
              <p className="text-xs text-sky-900/70 italic">
                A registration link will be added soon.
              </p>
            )}
          </div>
        ) : classItem.status === "open" && (
          classItem.externalSignupUrl ? (
            <div className="mt-6 rounded-lg border border-border bg-card p-5 space-y-3">
              <h4 className="font-heading text-lg font-bold text-foreground">Register</h4>
              <p className="text-sm text-muted-foreground">
                Registration for <strong>{classItem.title}</strong> is handled through our partner site.
              </p>
              <Button asChild className="w-full font-body font-bold">
                <a href={classItem.externalSignupUrl} target="_blank" rel="noopener noreferrer">
                  Sign Up Here <ExternalLink size={16} className="ml-2" />
                </a>
              </Button>
            </div>
          ) : (
            <ClassRegistrationForm
              classId={classItem.id}
              className={classItem.title}
              price={classItem.price}
              unitPrice={classItem.unitPrice}
              sessions={classItem.sessions}
              schedule={classItem.schedule}
              allowChildAgeZero={classItem.ages.toLowerCase().includes("all ages")}
              billingUnit={classItem.billingUnit}
            />
          )
        )}
        {instructorWanted && (
          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-5 space-y-3">
            <h4 className="font-heading text-lg font-bold text-amber-900">Instructor Wanted</h4>
            <p className="text-sm text-amber-900">
              We're looking for someone passionate about teaching this class. If that's
              you, apply below — it takes just a few minutes. Parents: you can still add
              your child to the interest list below.
            </p>
            <Button
              className="w-full font-body font-bold"
              onClick={() =>
                navigate(`/want-to-teach?class=${encodeURIComponent(classItem.title)}`)
              }
            >
              <GraduationCap size={16} className="mr-2" /> Teach This Class
            </Button>
          </div>
        )}
        {isComingSoon && classItem.id !== "teen-apprenticeship-program" && (
          <div className="mt-6 rounded-lg border border-sky-200 bg-sky-50 p-4">
            <p className="font-heading text-base font-bold text-sky-900">Coming Soon</p>
            <p className="text-sm text-sky-900">
              {/* Never assert a term when the class dates are still TBD. */}
              {String(classItem.startDate ?? "").trim().toUpperCase() === "TBD"
                ? "Dates are still being finalized."
                : (classItem && publicPeriodSuffix(classItem, nextPeriodLabel))
                  ? `Coming this ${publicPeriodSuffix(classItem, nextPeriodLabel)}.`
                  : "Coming soon."}
              {" "}Registration opens soon — join the interest list below and we'll notify you.
              {hasInterestGroups ? " Tell us which options your child would be interested in — you may select more than one." : ""}
            </p>
          </div>
        )}
        {/* Parent interest — available for every non-registering public state
            (Coming Soon, Instructor Wanted, Waitlist). Interest consumes no
            seats and creates no payment activity. */}
        {ctaRules.interest &&
          !hasInterestGroups &&
          classItem.id !== "teen-apprenticeship-program" && (
            <div className="mt-6 space-y-3">
              {instructorWanted && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
                  <p className="font-heading text-base font-bold text-sky-900">
                    Interested for My Child
                  </p>
                  <p className="text-sm text-sky-900">
                    Add your child to the interest list. We use this demand to prioritise
                    finding an instructor — it does not reserve a seat or charge anything.
                  </p>
                </div>
              )}
              {ctaRules.waitlist && (
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                  <p className="font-heading text-base font-bold text-purple-900">
                    Class is Full — Waitlist Open
                  </p>
                  <p className="text-sm text-purple-900">
                    Add your name below and we'll email you if a spot opens up. Your entry
                    does not enroll you until we confirm.
                  </p>
                </div>
              )}

              <InterestForm
                classId={classItem.id}
                className={classItem.title}
                sessions={classItem.sessions}
              />
            </div>
          )}





        {classItem.supplyList && (
          <>
            <Dialog open={supplyOpen} onOpenChange={setSupplyOpen}>
              <DialogContent className="max-w-lg bg-background max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Supply List</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  Items for <strong>{classItem.title}</strong>. Click any item to view it on Amazon.
                </p>
                {classItem.supplyList.note && (
                  <p className="text-sm text-foreground bg-muted/50 border border-border rounded-md p-3">
                    {classItem.supplyList.note}
                  </p>
                )}

                {classItem.supplyList.required && classItem.supplyList.required.length > 0 && (
                  <div className="mt-2 space-y-2">
                    <h4 className="font-heading text-base font-bold text-foreground">Required</h4>
                    <ul className="space-y-2">
                      {classItem.supplyList.required.map((item) => (
                        <li key={item.url} className="bg-card border border-border rounded-md p-3">
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1"
                          >
                            {item.name}
                            <ExternalLink size={14} />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {classItem.supplyList.recommended && classItem.supplyList.recommended.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h4 className="font-heading text-base font-bold text-foreground">Recommended</h4>
                    <p className="text-xs text-muted-foreground">
                      Optional — only if you'd like your student to have their own set of tools.
                    </p>
                    <ul className="space-y-2">
                      {classItem.supplyList.recommended.map((item) => (
                        <li key={item.url} className="bg-card border border-border rounded-md p-3">
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1"
                          >
                            {item.name}
                            <ExternalLink size={14} />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const InfoRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="flex items-center gap-2 bg-card rounded-md p-3 border border-border">
    <span className="text-primary">{icon}</span>
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold text-foreground">{value}</p>
    </div>
  </div>
);

export default ClassDetailDialog;
